<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
import { MousePointerClick } from "lucide-vue-next";
import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/episto-ui/empty-state";
import ResultsV2CardOptions from "./ResultsV2CardOptions.vue";
import ChartTypeGrid from "./CardOptionFields/ChartTypeGrid.vue";
import SectionCardOptions from "./SectionCardOptions.vue";
import { contentSubtype } from "./constants";
import { hasChartGrid } from "./cardOptionRules";
import { useCardTypeIdentity } from "@/composables/useCardTypeIdentity";
import type { ChartCard, ResultsCard } from "@/types";

const store = useResultsV2Store();
const cardsStore = useResultsV2CardsStore();
const { availableCards } = storeToRefs(store);
const { selectedCard: storeSelectedCard, openOptionSection } = storeToRefs(cardsStore);
const selectedCard = computed(() => storeSelectedCard.value as ResultsCard | null);

const { icon, label } = useCardTypeIdentity(selectedCard);

const isSection = computed(() => contentSubtype(selectedCard.value) === "section");
const showChartGrid = computed(() => {
  if (!["question", "recode"].includes(selectedCard.value?.card_type || "")) return false;
  return hasChartGrid((availableCards.value as ChartCard[]).find((c) => c.id === selectedCard.value?.card_id));
});

const optionsBody = ref<HTMLElement | null>(null);
watch(
  () => selectedCard.value?.id,
  async () => {
    await nextTick();
    const sections = optionsBody.value?.querySelectorAll<HTMLElement>("[data-option-section]") || [];
    const ids = [...sections].map((el) => el.dataset.optionSection || "");
    if (ids.length && openOptionSection.value && !ids.includes(openOptionSection.value)) openOptionSection.value = ids[0];
  },
  { immediate: true },
);
</script>

<template>
  <Card
    data-results-panel
    class="flex h-full flex-col overflow-hidden shadow-xs">
    <template v-if="selectedCard">
      <div class="shrink-0 space-y-3 border-b px-4 py-3">
        <div class="flex min-w-0 items-center gap-2">
          <span
            v-if="icon"
            class="flex size-6 shrink-0 items-center justify-center rounded bg-muted text-foreground">
            <component
              :is="icon"
              class="size-3.5" />
          </span>
          <span class="truncate text-13 font-semibold text-primary">{{ label }}</span>
        </div>
        <SectionCardOptions
          v-if="isSection"
          :card="selectedCard" />
        <ChartTypeGrid
          v-if="showChartGrid"
          :key="selectedCard.id"
          :card="selectedCard" />
      </div>
      <div
        ref="optionsBody"
        class="flex-1 space-y-6 overflow-y-auto p-4 pb-20">
        <p
          v-if="isSection"
          class="text-xs text-muted-foreground">
          A section only has a title. Collapse or reorder it from the dashboard.
        </p>
        <ResultsV2CardOptions
          v-else
          :card="selectedCard" />
      </div>
    </template>
    <EmptyState
      v-else
      :icon="MousePointerClick"
      title="Select a card to edit its options"
      size="sm"
      class="m-auto px-6" />
  </Card>
</template>
